import { roundTo } from '@utils/numberFormatters.js';
/**
 * Calculate gross quantity based on production count and unit packing.
 * @param production - The production count.
 * @param unitPacking - The unit packing value.
 * @returns The calculated gross quantity rounded to 4 decimal places, or null if inputs are invalid.
 */
export function calculateGrossQuantity(production, unitPacking) {
    if (production !== null && unitPacking !== null) {
        return roundTo(production * unitPacking, 4);
    }
    return null;
}
/**
 * Calculate net quantity based on net production count and unit packing.
 * @param netProduction - The net production count.
 * @param unitPacking - The unit packing value.
 * @returns The calculated net quantity rounded to 4 decimal places, or null if inputs are invalid.
 */
export function calculateNetQuantity(netProduction, unitPacking) {
    if (netProduction !== null && unitPacking !== null) {
        return roundTo(netProduction * unitPacking, 4);
    }
    return null;
}
/**
 * Calculate available time in minutes.
 * Available Time = Running Duration + Performance Downtime Duration + Availability Downtime Duration
 * @param runningDuration - Running duration in seconds.
 * @param performanceDowntime - Performance downtime duration in seconds.
 * @param availabilityDowntime - Availability downtime duration in seconds.
 * @returns The calculated available time in minutes.
 */
export function calculateAvailableTimeMin(runningDuration, performanceDowntime, availabilityDowntime) {
    if (runningDuration == null &&
        performanceDowntime == null &&
        availabilityDowntime == null) {
        return 0;
    }
    const running = runningDuration || 0;
    const performance = performanceDowntime || 0;
    const availability = availabilityDowntime || 0;
    return (running + performance + availability) / 60;
}
/**
 * Calculate operating time in minutes.
 * Operating Time = Running Duration + Performance Downtime Duration
 * @param runningDuration - Running duration in seconds.
 * @param performanceDowntime - Performance downtime duration in seconds.
 * @returns The calculated operating time in minutes.
 */
export function calculateOperatingTimeMin(runningDuration, performanceDowntime) {
    if (runningDuration == null && performanceDowntime == null) {
        return 0;
    }
    const running = runningDuration || 0;
    const performance = performanceDowntime || 0;
    return (running + performance) / 60;
}
/**
 * Calculate throughput (products/min or quantity/min).
 * Throughput = Production Count or Quantity / Available time in minutes
 * @param amount - The amount (production count or quantity).
 * @param time - The time in minutes.
 * @returns The calculated throughput, or 0 if time is 0 or invalid.
 */
export function calculateThroughput(amount, time) {
    if (time && time > 0) {
        return (amount != null ? amount : 0) / time;
    }
    return 0;
}
